import {
  IconChevronDown,
  IconChevronRight,
  IconLoader2,
  IconMail,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import type { NotificationEventType } from '@/api/notification-preferences';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

/** Strip redundant audience prefix from titles; section context already implies internal vs client. */
function formatNotificationEventLabel(eventType: NotificationEventType): string {
  if (eventType.isUmbrella) return 'All';
  const { code, name } = eventType;
  if (code.startsWith('INTERNAL_')) {
    const trimmed = name.replace(/^internal\s+/i, '').trim();
    return trimmed.length > 0 ? trimmed : name;
  }
  if (code.startsWith('BUYER_')) {
    const trimmed = name.replace(/^buyer\s+/i, '').trim();
    return trimmed.length > 0 ? trimmed : name;
  }
  return name;
}

export interface EmailPreferenceCardProps {
  email: string;
  selectedEventIds: number[];
  recipientRole: 'to' | 'cc';
  availableEventTypes: NotificationEventType[];
  onUpdate: (eventTypeIds: number[], recipientRole: 'to' | 'cc') => Promise<void>;
  onRemove: () => Promise<void>;
  isUpdating: boolean;
  defaultExpanded?: boolean;
}

export function EmailPreferenceCard({
  email,
  selectedEventIds,
  recipientRole,
  availableEventTypes,
  onUpdate,
  onRemove,
  isUpdating,
  defaultExpanded = false,
}: EmailPreferenceCardProps) {
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>(selectedEventIds);
  const [localRole, setLocalRole] = useState<'to' | 'cc'>(recipientRole);
  const [hasChanges, setHasChanges] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setLocalSelectedIds(selectedEventIds);
    setLocalRole(recipientRole);
    setHasChanges(false);
  }, [selectedEventIds, recipientRole]);

  // Get summary of selected notifications
  const getSelectionSummary = () => {
    const umbrellaEvent = availableEventTypes.find((et) => et.isUmbrella);
    if (umbrellaEvent && localSelectedIds.includes(umbrellaEvent.id)) {
      return 'All notifications';
    }
    const count = localSelectedIds.length;
    if (count === 0) return 'No notifications selected';
    return `${count} notification${count === 1 ? '' : 's'} selected`;
  };

  const handleCheckboxChange = (eventTypeId: number, checked: boolean) => {
    const eventType = availableEventTypes.find((et) => et.id === eventTypeId);
    const umbrellaId = availableEventTypes.find((et) => et.isUmbrella)?.id;

    if (!checked) {
      setLocalSelectedIds(localSelectedIds.filter((id) => id !== eventTypeId));
      setHasChanges(true);
      return;
    }

    if (eventType?.isUmbrella) {
      const otherUmbrellas = localSelectedIds.filter((id) =>
        availableEventTypes.find((et) => et.id === id && et.isUmbrella && et.id !== eventTypeId)
      );
      setLocalSelectedIds([eventTypeId, ...otherUmbrellas]);
    } else {
      const withoutUmbrella = localSelectedIds.filter((id) => id !== umbrellaId);
      setLocalSelectedIds([...withoutUmbrella, eventTypeId]);
    }

    setHasChanges(true);
  };

  const handleRoleChange = (role: 'to' | 'cc') => {
    setLocalRole(role);
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onUpdate(localSelectedIds, localRole);
    setHasChanges(false);
  };

  // Check if umbrella is selected
  const umbrellaEvent = availableEventTypes.find((et) => et.isUmbrella);
  const isUmbrellaSelected = umbrellaEvent ? localSelectedIds.includes(umbrellaEvent.id) : false;

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Collapsed Header - Always visible. The toggle and the remove button are
          siblings: a button nested in a button is invalid, and its name leaks into
          the toggle's. */}
      <div className="flex items-center hover:bg-muted transition-colors">
        <button
          type="button"
          className="flex flex-1 min-w-0 items-center gap-3 py-4 pl-4 pr-2 cursor-pointer text-left"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-center">
            {isExpanded ? (
              <IconChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            ) : (
              <IconChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
            )}
          </div>
          <IconMail className="h-5 w-5 text-muted-foreground" aria-hidden />
          <div className="flex-1 min-w-0">
            <span className="font-medium text-foreground block truncate">{email}</span>
            {!isExpanded && (
              <span className="text-xs text-muted-foreground">
                {getSelectionSummary()} &middot;{' '}
                <span
                  className={
                    localRole === 'cc'
                      ? 'text-amber-700 dark:text-amber-400 font-medium'
                      : 'text-blue-600 dark:text-blue-400 font-medium'
                  }
                >
                  {localRole === 'cc' ? 'CC' : 'To'}
                </span>
              </span>
            )}
          </div>
        </button>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Remove ${email}`}
          onClick={() => onRemove()}
          disabled={isUpdating}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-950/40 mr-4"
        >
          <IconTrash className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-border">
          {/* Recipient role toggle */}
          <div className="pt-4 pb-3 border-b border-border mb-3">
            <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
              Recipient type
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleRoleChange('to')}
                disabled={isUpdating}
                className={`px-3 py-1.5 text-sm rounded-md border font-medium transition-colors ${
                  localRole === 'to'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-muted text-foreground border-border hover:border-border/80'
                }`}
              >
                To
              </button>
              <button
                type="button"
                onClick={() => handleRoleChange('cc')}
                disabled={isUpdating}
                className={`px-3 py-1.5 text-sm rounded-md border font-medium transition-colors ${
                  localRole === 'cc'
                    ? 'bg-amber-700 text-white border-amber-700'
                    : 'bg-muted text-foreground border-border hover:border-border/80'
                }`}
              >
                CC
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {localRole === 'to'
                ? 'Primary recipient — address appears in the To field.'
                : 'Carbon copy — address appears in the CC field; all recipients can see it.'}
            </p>
          </div>

          <div className="space-y-3">
            {availableEventTypes.map((eventType) => {
              const isChecked = localSelectedIds.includes(eventType.id);
              const isDisabled = !eventType.isUmbrella && isUmbrellaSelected;

              return (
                <div key={eventType.id} className="flex items-start gap-3">
                  <Checkbox
                    id={`${email}-${eventType.id}`}
                    checked={isChecked || isDisabled}
                    disabled={isDisabled || isUpdating}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(eventType.id, checked === true)
                    }
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor={`${email}-${eventType.id}`}
                      className={`text-sm font-medium cursor-pointer ${
                        isDisabled ? 'text-muted-foreground' : 'text-foreground'
                      }`}
                    >
                      {formatNotificationEventLabel(eventType)}
                      {eventType.isUmbrella && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                          (All notifications)
                        </span>
                      )}
                    </Label>
                    {eventType.description && (
                      <p className="text-xs mt-0.5 text-muted-foreground">
                        {eventType.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {hasChanges && (
            <div className="mt-4 pt-4 border-t flex justify-end">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isUpdating}
                className="bg-gray-900 hover:bg-gray-800 text-white dark:bg-secondary dark:hover:bg-secondary/80 dark:text-secondary-foreground"
              >
                {isUpdating ? (
                  <>
                    <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EmailPreferenceCard;
